import React, { useState, useEffect } from 'react';
import { FiX as X, FiSave as Save, FiFileText as FileText } from 'react-icons/fi';
import HelpTooltip from '../../../../../components/common/HelpTooltip';

/**
 * Editor for document-type dataset items
 * Used for RAG content like FAQs, knowledge base articles, documentation
 */

interface DocTypeSpecificData {
  title: string;
  source: string;
  url: string;
  category: string;
}

interface DocMetadata {
  tags: string[];
}

interface DocFormData {
  name: string;
  content: string;
  type_specific_data: DocTypeSpecificData;
  metadata: DocMetadata;
  embedding_text: string;
}

interface DatasetItem {
  name?: string;
  content?: string;
  type_specific_data?: Partial<DocTypeSpecificData>;
  metadata?: Partial<DocMetadata>;
  embedding_text?: string;
}

interface SavePayload {
  item_type: string;
  content: string;
  name: string;
  type_specific_data: DocTypeSpecificData;
  metadata: DocMetadata;
  embedding_text: string | null;
}

interface DocumentItemEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload: SavePayload) => void;
  item?: DatasetItem | null;
  saving?: boolean;
}

const DocumentItemEditor: React.FC<DocumentItemEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  item = null,
  saving = false,
}) => {
  const [formData, setFormData] = useState<DocFormData>({
    name: '',
    content: '',
    type_specific_data: { title: '', source: '', url: '', category: '' },
    metadata: { tags: [] },
    embedding_text: '',
  });

  const [tagsInput, setTagsInput] = useState('');

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        content: item.content || '',
        type_specific_data: {
          title: item.type_specific_data?.title || '',
          source: item.type_specific_data?.source || '',
          url: item.type_specific_data?.url || '',
          category: item.type_specific_data?.category || '',
        },
        metadata: { tags: item.metadata?.tags || [] },
        embedding_text: item.embedding_text || '',
      });
      setTagsInput((item.metadata?.tags || []).join(', '));
    } else {
      setFormData({
        name: '',
        content: '',
        type_specific_data: { title: '', source: '', url: '', category: '' },
        metadata: { tags: [] },
        embedding_text: '',
      });
      setTagsInput('');
    }
  }, [item, isOpen]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const tags = tagsInput?.split(',').map((t) => t.trim()).filter((t) => t) ?? [];

    onSave({
      item_type: 'document',
      content: formData.content,
      name: formData.name || formData.type_specific_data.title || 'Untitled Document',
      type_specific_data: formData.type_specific_data,
      metadata: { ...formData.metadata, tags },
      embedding_text: formData.embedding_text || null,
    });
  };

  if (!isOpen) return null;

  let submitButtonLabel: string;
  if (saving) {
    submitButtonLabel = 'Saving...';
  } else if (item) {
    submitButtonLabel = 'Update';
  } else {
    submitButtonLabel = 'Create';
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {item ? 'Edit Document' : 'Add Document'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title
              </label>
              <input id="title"
                type="text"
                value={formData.type_specific_data.title}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type_specific_data: { ...formData.type_specific_data, title: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                placeholder="e.g., How to reset your password"
              />
            </div>

            {/* Content */}
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content *
                <HelpTooltip text="The main document text. This is what will be indexed for RAG retrieval and used during evaluations." />
              </label>
              <textarea id="content"
                required
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                rows={8}
                placeholder="Enter the document content here..."
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This is the main text that will be used for RAG retrieval
              </p>
            </div>

            {/* Source & URL Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="source" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Source
                </label>
                <input id="source"
                  type="text"
                  value={formData.type_specific_data.source}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type_specific_data: { ...formData.type_specific_data, source: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 transition-colors"
                  placeholder="e.g., Help Center, Documentation"
                />
              </div>
              <div>
                <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  URL (optional)
                </label>
                <input id="url"
                  type="url"
                  value={formData.type_specific_data.url}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type_specific_data: { ...formData.type_specific_data, url: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 transition-colors"
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <input id="category"
                type="text"
                value={formData.type_specific_data.category}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    type_specific_data: { ...formData.type_specific_data, category: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                placeholder="e.g., FAQ, Tutorial, Policy"
              />
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tags-comma-separated" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tags (comma-separated)
              </label>
              <input id="tags-comma-separated"
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                placeholder="e.g., billing, account, getting-started"
              />
            </div>

            {/* Custom Embedding Text (Advanced) */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <label htmlFor="custom-embedding-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Custom Embedding Text (optional)
                <HelpTooltip text="Custom text for vector search indexing. Leave blank to use the main content. Useful for adding keywords or summaries that improve retrieval." />
              </label>
              <textarea id="custom-embedding-text"
                value={formData.embedding_text}
                onChange={(e) => setFormData({ ...formData, embedding_text: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                rows={3}
                placeholder="Leave empty to use the content for embeddings"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                If set, this text will be used for vector embeddings instead of the main content
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                       transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg
                       hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors"
            >
              <Save className="w-4 h-4" />
              {submitButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentItemEditor;
